import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment";
import { Box, Typography } from "@mui/material";

import { ActionButton, CardLayout, CrudTable, InputField, SelectField, Title, ToggleButton } from "../v3";
import { AccountService, HumanResourceService, ProjectService } from "../../services";
import { dateWithCurrentTime, formatAddWorkPayload, formatUpdateWorkPayload, useHasPermission } from "../../utils";
import { validateAdvancePayment, workValidationRules } from "../../pages/worker/validation";
import { ENABLE_WORK_BACKDATE_ENTRY, ENABLE_WORKER_ADVANCE_PAYMENT, VIEW_ACCOUNT } from "../../constants";
import { getWorkTableColumns, GroupWorkParticularsColumns } from "../../pages/worker/constants";
import { AddEditParticular } from "./AddEditParticular.component";
import { markTableReset } from "../../utils/tableStateStorage";

export interface AddEditWorkFormProps {
  workId?: string | number | null;
  isGroupWorkerMode?: boolean;
}

export const AddEditWorkForm = ({ workId, isGroupWorkerMode = false }: AddEditWorkFormProps) => {
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [selectedWorkers, setSelectedWorkers] = useState<any[]>([]);
  const [selectedProjectStage, setSelectedProjectStage] = useState<any>(null);
  const [selectedAccount, setSelectedAccount] = useState<any>(null);
  const [stageIds, setStageIds] = useState<any>([]);
  const [projectStages, setProjectStages] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [errors, setErrors] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [currentWorkerOvertimeRate, setCurrentWorkerOvertimeRate] = useState(0);
  const [isPaid, setIsPaid] = useState(false);
  const [workDetails, setWorkDetails] = useState<Record<string, any>>({
    workerId: undefined,
    projectId: undefined,
    taskId: undefined,
    projectStageId: undefined,
    date: moment().format("YYYY-MM-DD"),
    wage: 0,
    overtimeHours: undefined,
    overtimeRate: undefined,
    description: undefined,
  });
  const [paymentDetails, setPaymentDetails] = useState<Record<string, any>>({
    fromAccountId: undefined,
    paidAmount: undefined,
  });
  const [isGroupWork, setIsGroupWork] = useState<boolean>(isGroupWorkerMode ?? false);
  const [particulars, setParticulars] = useState<any[]>([]);
  const [openParticularModal, setOpenParticularModal] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const workerId = searchParams.get("workerId");
  const projectId = searchParams.get("projectId");

  const { user } = useSelector((state: any) => state.user);
  const { ownerUserId: companyAdminId } = user.company;
  const hasViewAccount = useHasPermission(VIEW_ACCOUNT);
  const enableBackDate = useHasPermission(ENABLE_WORK_BACKDATE_ENTRY);
  const enableAdvancePayment = useHasPermission(ENABLE_WORKER_ADVANCE_PAYMENT);

  useEffect(() => {
    if (workId) {
      fetchWorkDetails(workId);
    } else if (projectId) {
      fetchProject();
    }
    fetchAccounts();
  }, []);

  const isSingleMode = !!workerId || !!workId || isGroupWork;

  useEffect(() => {
    if (workerId) {
      fetchData(workerId);
    }
  }, [workerId]);

  useEffect(() => {
    if (!workDetails.workerId) return;
    fetchData(workDetails?.workerId);
  }, [workDetails.workerId]);

  const stageFilter = useMemo(
    () => ({
      ids: stageIds,
      sortBy: "name",
      sortOrder: "asc",
    }),
    [stageIds],
  );

  const fetchData = async (workerId: any) => {
    try {
      const { data } = await HumanResourceService.getEmployeeById(workerId, { includeDeleted: true });
      setSelectedWorkers([data]);
      setWorkDetails((prev) => ({
        ...prev,
        workerId: data.id,
        wage: workId ? prev.wage : Number(data.salary),
        overtimeRate: Number(data.overtimeHourlyRate),
      }));
      setCurrentWorkerOvertimeRate(Number(data.overtimeHourlyRate));
    } catch (error) {
      console.error("Error fetching worker details:", error);
      toast.error("Failed to fetch worker details. Please try again.");
    }
  };

  const fetchWorkDetails = async (workId: any) => {
    try {
      const { data: work } = await HumanResourceService.getWorkById(workId);
      // fetch project stages
      if (work?.projectId) fetchProjectStages(work?.projectId);

      // fetch stages
      if (work?.projectStageId) {
        try {
          const { data: stage } = await ProjectService.getStageById(work?.projectStage?.stageId);
          setSelectedProjectStage(stage);
        } catch (error) {
          toast.error("Failed to fetch stage details");
          console.error("🚀 ~ fetchData ~ error:", error);
        }
      }
      setWorkDetails({ ...work, date: moment(work.date).format("YYYY-MM-DD") });
      setSelectedProject(work.project);

      if (work.isGroupWork) {
        setIsGroupWork(true);
        if (work.particulars) {
          setParticulars(
            work.particulars.map((p: any) => ({
              id: p.particularId,
              particularId: p.particularId,
              name: p.name,
              rate: p.rate ?? 0,
              quantity: p.quantity || "",
            })),
          );
        }
      }
    } catch (error) {
      console.error("Error fetching work details:", error);
      toast.error("Failed to fetch work details.");
    }
  };

  const fetchAccounts = async () => {
    try {
      const { data: accounts } = await AccountService.getAdminAndUserAccounts({
        hasViewAccount,
        user,
        companyAdminId,
      });
      setAccounts(accounts.rows || []);
      setSelectedAccount(accounts.rows[0]);
      setPaymentDetails((prev) => ({
        ...prev,
        fromAccountId: accounts.rows[0]?.id,
      }));
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchProject = async () => {
    try {
      const { data: project } = await ProjectService.getProjectById(projectId);
      handleProjectChange(project);
    } catch (error) {
      console.error("🚀 ~ fetchProject ~ error:", error);
    }
  };

  const fetchProjectStages = async (projectId) => {
    try {
      const { data: first } = await ProjectService.getProjectStages({
        projectId,
        page: 1,
        limit: 10,
      });

      const { data } = await ProjectService.getProjectStages({
        projectId,
        page: 1,
        limit: first.count,
      });

      const stages = [...new Set(data.rows.map((item) => item.stageId))];

      setStageIds(stages.join(","));
      setProjectStages(data.rows);
    } catch (error) {
      console.error("🚀 ~ fetchProjectStages ~ error:", error);
    }
  };

  const updateDetailsState = (event: any) => {
    const { name, value } = event.target;
    setWorkDetails((prev) => ({ ...prev, [name]: value }));

    if (name === "overtimeHours") {
      const num = parseFloat(value);

      if (value !== "" && (isNaN(num) || (num * 10) % 5 !== 0)) {
        setErrors((prev) => ({
          ...prev,
          overtimeHours: "must be a multiple of 0.5",
        }));
      } else if (value !== "" && num < 0.5) {
        setErrors((prev) => ({
          ...prev,
          overtimeHours: "must be greater/equal to 0.5",
        }));
      } else {
        setErrors((prev) => {
          const { overtimeHours, ...rest } = prev;
          return rest;
        });
      }
    }
  };

  const handleWorkerChange = (selected: any) => {
    const selectedArray = Array.isArray(selected) ? selected : selected ? [selected] : [];
    const validWorkers = selectedArray.filter((worker) => worker && worker.id && !worker.isAddButton);

    if (isGroupWork) {
      const newWorkerId = validWorkers[0]?.id;
      const oldWorkerId = selectedWorkers[0]?.id;
      if (newWorkerId !== oldWorkerId) {
        setParticulars([]);
      }
    }

    setSelectedWorkers((prevWorkers) => {
      const prevMap = new Map(prevWorkers.map((worker) => [worker.id, worker]));

      return validWorkers.map((worker) => {
        const existing = prevMap.get(worker.id);

        if (existing) return existing;

        return {
          id: worker.id,
          name: worker.name,
          salary: worker.salary ?? 0,
          overtimeHourlyRate: worker.overtimeHourlyRate ?? null,
          overtimeHours: null,
          paid: false,
          accountId: null,
          paidAmount: null,
        };
      });
    });

    if (validWorkers.length === 0) setCurrentWorkerOvertimeRate(0);
    setWorkDetails((prev) => ({ ...prev, workerId: validWorkers?.[0]?.id || null }));
  };

  const handleProjectChange = (selected: any) => {
    setStageIds([]);
    setSelectedProjectStage(null);
    setSelectedProject(selected);
    setWorkDetails((prev) => ({ ...prev, projectId: selected?.id, projectStageId: null }));
    if (selected?.id) fetchProjectStages(selected.id);
  };

  const handleProjectStageChange = (selected: any) => {
    const projectStage = projectStages.find((item: any) => item?.stageId === selected?.id);
    setSelectedProjectStage(selected);
    setWorkDetails((prev) => ({ ...prev, projectStageId: projectStage?.id || null }));
  };

  const handleAccountChange = (selected: any) => {
    setSelectedAccount(selected);
    setPaymentDetails((prev) => ({ ...prev, fromAccountId: selected?.id || null }));
  };

  const totalWageAndOvertime = isGroupWork
    ? particulars.reduce((acc, curr) => acc + Number(curr.rate || 0) * Number(curr.quantity || 0), 0)
    : workDetails.overtimeHours
      ? Number(workDetails.wage) + Number(workDetails.overtimeHours * workDetails.overtimeRate)
      : workDetails.wage;

  const handlePaymentDetailsChange = (event: any) => {
    const { name, value } = event.target;
    setPaymentDetails((prev) => ({ ...prev, [name]: value }));

    if (name === "paidAmount") {
      let amountError;

      if (value !== "" && Number(value) < 1) {
        amountError = "Amount must be greater than 0";
      } else {
        amountError = validateAdvancePayment({
          amount: value,
          balance: Number(formatBalance(selectedWorkers?.[0]?.account?.balance)) + Number(totalWageAndOvertime),
          enableAdvancePayment,
        });
      }

      setErrors((prev) => {
        const nextErrors = { ...prev };

        if (amountError) nextErrors.paidAmount = amountError;
        else delete nextErrors.paidAmount;

        return nextErrors;
      });
    }
  };

  const handleCancelClick = () => {
    handleClearForm();
    navigate(-1);
  };

  const handleParticularSuccess = (newParticular: any) => {
    const particularId = newParticular?.id ?? newParticular?.particularId;
    if (!particularId) return;

    setParticulars((prev) => {
      if (prev.some((p) => p.particularId === particularId)) {
        return prev;
      }
      return [
        ...prev,
        {
          id: particularId,
          particularId: particularId,
          name: newParticular.name,
          rate: newParticular.defaultRate || 0,
          quantity: "",
        },
      ];
    });
  };

  const handleClearForm = () => {
    setErrors({});
    setWorkDetails({
      workerId: undefined,
      projectId: undefined,
      taskId: undefined,
      projectStageId: undefined,
      date: moment().format("YYYY-MM-DD"),
      wage: 0,
      overtimeHours: undefined,
      overtimeRate: undefined,
      description: undefined,
    });

    setPaymentDetails({
      fromAccountId: accounts[0]?.id,
      paidAmount: undefined,
    });
    setIsPaid(false);
    setSelectedAccount(accounts[0] || null);
    setParticulars([]);
    setSelectedWorkers([]);
    setSelectedProject(null);
    setSelectedProjectStage(null);
    if (!workerId || !workId) setCurrentWorkerOvertimeRate(0);
  };

  const formatBalance = (value: any) => {
    const num = Number(value);

    if (isNaN(num) || num === 0) return 0;
    return (-num).toFixed(2);
  };

  const getAmountLabel = ({ enableAdvancePayment, balance, amount = 0 }: any) => {
    if (!enableAdvancePayment && balance !== null) {
      const totalAmount = Number(amount) + Number(balance);
      return `Paid Amount (Balance: ${Number(totalAmount).toLocaleString()})`;
    }

    return "Paid Amount";
  };

  const handleSaveWork = async () => {
    setErrors({});
    if (isSingleMode) {
      let customErrors: Record<string, string> = {};
      if (!workDetails.workerId) customErrors.workerId = "Please select a worker";
      if (!workDetails.projectId) customErrors.projectId = "Please select a project";
      if (!workDetails.date) customErrors.date = "Date is required";

      const validationErrors = workValidationRules(
        {
          ...workDetails,
          isPaid,
          isGroupWork,
          ...paymentDetails,
        },
        {
          balance: Number(formatBalance(selectedWorkers?.[0]?.account?.balance)) + Number(totalWageAndOvertime),
          enableAdvancePayment,
        },
      );

      if (isGroupWork) {
        if (!particulars || particulars.length === 0) {
          customErrors.particulars = "At least one particular is required for group work";
        } else {
          const hasInvalid = particulars.some(
            (p) => !Number(p.rate) || Number(p.rate) <= 0 || !Number(p.quantity) || Number(p.quantity) <= 0,
          );
          if (hasInvalid) {
            toast.error("Please enter valid rate and quantity for all particulars");
            return;
          }
        }
      }

      if (Object.keys(validationErrors).length > 0 || Object.keys(customErrors).length > 0) {
        setErrors({ ...validationErrors, ...customErrors });
        return;
      }
    }

    if (!isSingleMode) {
      const bulkErrors: Record<string, string> = {};

      if (!workDetails.date) bulkErrors.date = "Date is required";
      if (!workDetails.projectId) bulkErrors.projectId = "Please select a project";
      if (!selectedWorkers.length) bulkErrors.workerId = "Please select at least one worker";

      if (Object.keys(bulkErrors).length) {
        setErrors(bulkErrors);
        return;
      }
      let hasRowErrors = false;

      const validatedRows = selectedWorkers.map((worker: any) => {
        let salaryError: boolean | string = false;
        let overtimeError: boolean | string = false;
        let accountError: boolean | string = false;
        let paidAmountError: boolean | string = false;

        // Salary validation
        if (!worker.salary || Number(worker.salary) <= 0) {
          salaryError = true;
          hasRowErrors = true;
        }

        // Overtime validation
        if (worker.overtimeHours !== "" && worker.overtimeHours != null) {
          const hours = Number(worker.overtimeHours);

          if (isNaN(hours) || (hours * 10) % 5 !== 0) {
            overtimeError = "Must be a multiple of 0.5";
            hasRowErrors = true;
          } else if (hours < 0.5) {
            overtimeError = "Must be greater/equal to 0.5";
            hasRowErrors = true;
          }
        }

        // Paid validation
        if (worker.paid) {
          if (!worker.accountId) {
            accountError = true;
            hasRowErrors = true;
          }

          if (!worker.paidAmount || Number(worker.paidAmount) <= 0) {
            paidAmountError = true;
            hasRowErrors = true;
          }
        }

        return {
          ...worker,
          salaryError,
          overtimeError,
          accountError,
          paidAmountError,
        };
      });

      if (hasRowErrors) {
        setSelectedWorkers(validatedRows);
        return;
      }
    }

    try {
      setLoading(true);
      const payloadDetails = { ...workDetails, isGroupWork, particulars };
      if (workId) {
        const updateWorkPayload = formatUpdateWorkPayload(payloadDetails);
        await HumanResourceService.updateWork(workId, updateWorkPayload);
      } else {
        if (isSingleMode) {
          const createWorkPayload = formatAddWorkPayload({
            workDetails: payloadDetails,
            paymentDetails: isPaid ? paymentDetails : {},
            isPaid,
          });
          await HumanResourceService.createWork(createWorkPayload);
        } else {
          const bulkPayload = selectedWorkers.map((worker: any) => {
            const basePayload: Record<string, any> = {
              date: dateWithCurrentTime(workDetails.date),
              workerId: worker.id,
              wage: Number(worker.salary),
              description: workDetails.description ?? null,
              projectId: workDetails.projectId,
              taskId: workDetails.taskId ?? null,
              projectStageId: workDetails.projectStageId ?? null,
              isPaid: Boolean(worker.paid),
              paidAmount: worker.paid ? Number(worker.paidAmount) : null,
              fromAccountId: worker.paid ? worker.accountId : null,
            };

            // only attach overtime fields if overtimeHours exists
            if (worker.overtimeHours) {
              basePayload.overtimeHours = Number(worker.overtimeHours);
              basePayload.overtimeRate = Number(worker.overtimeHourlyRate);
            }

            return basePayload;
          });

          await HumanResourceService.createBulkWork({
            works: bulkPayload,
          });
        }
      }
      toast.success(`Work ${workId ? "updated" : "added"} successfully`);

      handleClearForm();
      if (workerId || workId) {
        // A new record would be hidden by the filters the list had, so it opens fresh; after an edit they're kept.
        if (!workId) markTableReset();
        navigate(-1);
      } else if (projectId) navigate(`/projects/${projectId}?tab=works`);
    } catch (error: any) {
      let errorMessage = `Error ${workId ? "updating" : "adding"} work`;
      const response = error?.response?.data;

      if (Array.isArray(response?.message)) {
        errorMessage = response.message.join(", ");
      } else if (typeof response?.message === "string") {
        errorMessage = response.message;
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CardLayout>
        <Box display="flex" gap={2}>
          <SelectField
            label="Worker"
            value={isSingleMode ? selectedWorkers?.[0] || null : selectedWorkers}
            getData={HumanResourceService.getEmployees}
            getDataStaticFilters={{
              active: true,
              type: "WORKER",
              sortBy: "name",
              sortOrder: "asc",
              includeBalance: true,
              ...(isGroupWork ? { isGroupWorker: true } : {}),
            }}
            disabled={!!workerId || !!workId}
            required
            multiSelect={!isSingleMode}
            limitTags={5}
            error={!!errors.workerId}
            helperText={errors.workerId}
            onChange={handleWorkerChange}
            style={{ width: "100%" }}
          />
          {isSingleMode && !isGroupWork && (
            <>
              <InputField
                required
                label="Wage"
                name="wage"
                type="number"
                value={workDetails.wage}
                onChange={updateDetailsState}
                error={!!errors.wage}
                helperText={errors.wage}
                fullWidth
              />
              <InputField
                label={`Overtime Hours (₹${currentWorkerOvertimeRate} per hour)`}
                name="overtimeHours"
                value={workDetails.overtimeHours}
                onChange={updateDetailsState}
                type="number"
                error={!!errors.overtimeHours}
                helperText={errors.overtimeHours}
                disabled={currentWorkerOvertimeRate === 0}
                fullWidth
              />
            </>
          )}
        </Box>
        <Box display="flex" gap={2}>
          <InputField
            required
            label="Date"
            name="date"
            type="date"
            value={workDetails.date}
            onChange={updateDetailsState}
            error={!!errors.date}
            helperText={errors.date}
            fullWidth
            inputSlotProps={{
              htmlInput: {
                min: enableBackDate ? undefined : moment().format("YYYY-MM-DD"),
                onKeyDown: enableBackDate ? undefined : (e: any) => e.preventDefault(),
              },
            }}
          />
          <SelectField
            label="Project"
            value={selectedProject}
            getData={ProjectService.getProjectList}
            getDataStaticFilters={{ status: ["INPROGRESS"], sortBy: "name", sortOrder: "asc" }}
            disabled={!!projectId}
            required
            error={!!errors.projectId}
            helperText={errors.projectId}
            onChange={handleProjectChange}
            style={{ width: "100%" }}
          />
          <SelectField
            label="Stage"
            value={selectedProjectStage}
            getData={ProjectService.getStages}
            getDataStaticFilters={stageFilter}
            disabled={stageIds.length === 0}
            error={!!errors.projectStageId}
            helperText={errors.projectStageId}
            onChange={handleProjectStageChange}
            style={{ width: "100%" }}
          />
        </Box>
        <Box display="flex" gap={2}>
          <InputField
            label="Description"
            name="description"
            value={workDetails.description}
            onChange={updateDetailsState}
            fullWidth
            autoComplete="on"
          />
        </Box>

        {isSingleMode && isGroupWork && workDetails.workerId && (
          <Box display="flex" flexDirection="column" gap={2} mb={2}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Add Particulars
            </Typography>
            <SelectField
              label={`Particulars (${particulars.length})`}
              value={particulars.map((p) => ({ id: p.particularId, name: p.name, defaultRate: p.rate }))}
              getData={HumanResourceService.getWorkerParticulars}
              getDataStaticFilters={{ workerId: workDetails.workerId, limit: 100 }}
              multiSelect={true}
              error={!!errors.particulars}
              helperText={errors.particulars}
              placeholder={particulars.length > 0 ? `${particulars.length} Particular(s) Selected` : "Select Particular Items"}
              onChange={(selected: any[]) => {
                setParticulars((prev) =>
                  selected
                    .filter((item: any) => item?.id && !item.isAddButton)
                    .map((item: any) => {
                      const existing = prev.find((p) => p.particularId === item.id);
                      if (existing) return existing;
                      return {
                        id: item.id,
                        particularId: item.id,
                        name: item.name,
                        rate: item.defaultRate || 0,
                        quantity: "",
                      };
                    }),
                );
              }}
              addButtonText="Add Particular"
              showAddButton={true}
              onAddClick={() => setOpenParticularModal(true)}
              style={{ width: "100%" }}
            />

            {particulars.length > 0 && (
              <CrudTable
                loading={loading}
                rows={particulars}
                setRows={setParticulars}
                columns={GroupWorkParticularsColumns()}
                hidePagination
              />
            )}
          </Box>
        )}

        {isSingleMode && (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" ml={1}>
              {!workId && (
                <ToggleButton
                  label="Paid"
                  checked={isPaid}
                  onToggleChange={(e?: any) => {
                    const checked = e?.target?.checked;
                    setIsPaid(checked);
                    setPaymentDetails((prev) => ({
                      ...prev,
                      paidAmount: checked ? totalWageAndOvertime : undefined,
                    }));
                  }}
                />
              )}
              <Title textVariant="p" title={`Total: ${totalWageAndOvertime}`} style={{ fontWeight: "bold" }} />
            </Box>

            {isPaid && (
              <Box display="flex" gap={2} mt={2}>
                <SelectField
                  label="Account"
                  value={selectedAccount}
                  getData={AccountService.getAdminAndUserAccounts}
                  getDataStaticFilters={{ hasViewAccount, user, companyAdminId }}
                  required
                  error={!!errors.fromAccountId}
                  helperText={errors.fromAccountId}
                  onChange={handleAccountChange}
                  style={{ width: "100%" }}
                />
                <InputField
                  required
                  label={getAmountLabel({
                    enableAdvancePayment,
                    balance: formatBalance(selectedWorkers?.[0]?.account?.balance),
                    amount: totalWageAndOvertime,
                  })}
                  name="paidAmount"
                  type="number"
                  value={paymentDetails.paidAmount}
                  onChange={handlePaymentDetailsChange}
                  error={!!errors.paidAmount}
                  helperText={errors.paidAmount}
                  style={{ width: "100%" }}
                />
              </Box>
            )}

            <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
              <ActionButton buttonText="cancel" variant="outlined" onButtonClick={handleCancelClick} />
              <ActionButton buttonText="save" onButtonClick={handleSaveWork} disabled={loading} />
            </Box>
          </>
        )}
      </CardLayout>

      {!isSingleMode && (
        <CardLayout>
          <CrudTable
            rows={selectedWorkers}
            setRows={setSelectedWorkers}
            columns={getWorkTableColumns(hasViewAccount, user, companyAdminId, setSelectedWorkers, selectedAccount)}
            hidePagination
          />
          <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
            <ActionButton buttonText="cancel" variant="outlined" onButtonClick={handleCancelClick} />
            <ActionButton buttonText="save" onButtonClick={handleSaveWork} disabled={loading} />
          </Box>
        </CardLayout>
      )}

      <AddEditParticular
        open={openParticularModal}
        onClose={() => setOpenParticularModal(false)}
        onSuccess={handleParticularSuccess}
        workerId={workDetails.workerId}
      />
    </>
  );
};
